
export type LoyaltyStatus = 'Traveler' | 'World Traveler' | 'Legend';

export interface Contact {
  id: string;
  name: string;
  email: string;
  loyalty_status: LoyaltyStatus;
  loyalty_points_balance: number;
  loyalty_points_lifetime_earned: number;
  loyalty_points_lifetime_redeemed: number;
  loyalty_total_spend_last_24m: number;
  loyalty_referral_code: string;
  loyalty_referral_count: number;
  check_in_streak: number;
  last_check_in: string;
}

export interface Booking {
  id: string;
  trip_name: string;
  destination: string;
  country: string;
  region: 'balkans' | 'western_europe' | 'central_europe' | 'mediterranean' | 'scandinavia' | 'eastern_europe';
  start_date: string;
  end_date: string;
  status: 'Završeno' | 'Predstojeće' | 'Otkazano';
  amount_eur: number;
  points_earned: number;
  points_redeemed: number;
}

export interface PointTransaction {
  id: string;
  date: string;
  type: 'Zarada - Putovanje' | 'Zarada - Bonus' | 'Zarada - Izazov' | 'Zarada - Check-in' | 'Zarada - Spin' | 'Iskorišćeno' | 'Isteklo';
  description: string;
  points: number;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  reward_points: number;
  icon: string;
  unlocked_date?: string;
  is_unlocked: boolean;
  category: 'putovanje' | 'društveno' | 'istraživač' | 'potrošač' | 'sezona';
}

// === GAMIFICATION TYPES ===

export interface DailyCheckIn {
  date: string;
  points: number;
  streakDay: number;
}

export interface TravelStamp {
  id: string;
  destination: string;
  country: string;
  region: 'balkans' | 'western_europe' | 'central_europe' | 'mediterranean' | 'scandinavia' | 'eastern_europe';
  date: string;
  emoji: string;
  tripName: string;
}

export interface SeasonalChallenge {
  id: string;
  title: string;
  description: string;
  icon: string;
  type: 'seasonal' | 'social' | 'explorer' | 'spender';
  reward: number;
  progress: number;
  target: number;
  deadline: string;
  completed: boolean;
}

export interface BingoCell {
  id: number;
  destination: string;
  country: string;
  visited: boolean;
}

export interface LeaderboardEntry {
  rank: number;
  name: string;
  initials: string;
  points: number;
  level: LoyaltyStatus;
  isCurrentUser: boolean;
  trips: number;
}

export interface SpinWheelPrize {
  id: number;
  label: string;
  points: number;
  color: string;
}

export interface Experience {
  id: string;
  title: string;
  description: string;
  destination: string;
  category: 'food' | 'adventure' | 'culture' | 'nature' | 'nightlife' | 'guide';
  price: number;
  bonusPoints: number;
  pointsMultiplier: number;
  emoji: string;
  rating: number;
  duration: string;
  popular: boolean;
}

export interface PointsMultiplierEvent {
  id: string;
  title: string;
  description: string;
  multiplier: number;
  startDate: string;
  endDate: string;
  active: boolean;
  type: 'flash' | 'seasonal' | 'partner';
  emoji: string;
}

export interface TravelBundle {
  id: string;
  name: string;
  trips: string[];
  originalPrice: number;
  bundlePrice: number;
  bonusPoints: number;
  pointsMultiplier: number;
  emoji: string;
}

// === MOCK DATA (matched to Go2 Event d.o.o. real products) ===

export const MOCK_USER: Contact = {
  id: 'user-123',
  name: 'Aleksa Putniković',
  email: 'aleksa@example.com',
  loyalty_status: 'World Traveler',
  loyalty_points_balance: 1250,
  loyalty_points_lifetime_earned: 2450,
  loyalty_points_lifetime_redeemed: 1200,
  loyalty_total_spend_last_24m: 1150,
  loyalty_referral_code: 'ALEKSA-GO2-2026',
  loyalty_referral_count: 3,
  check_in_streak: 5,
  last_check_in: '2026-02-11',
};

export const MOCK_BOOKINGS: Booking[] = [
  {
    id: 'b-1',
    trip_name: 'Go2 Kavos Leto 2025',
    destination: 'Kavos (Krf)',
    country: 'Grčka',
    region: 'mediterranean',
    start_date: '2025-07-10',
    end_date: '2025-07-20',
    status: 'Završeno',
    amount_eur: 349,
    points_earned: 349,
    points_redeemed: 0,
  },
  {
    id: 'b-2',
    trip_name: 'Go2 Grudvanje Les Orres',
    destination: 'Les Orres',
    country: 'Francuska',
    region: 'western_europe',
    start_date: '2026-01-10',
    end_date: '2026-01-19',
    status: 'Završeno',
    amount_eur: 449,
    points_earned: 539,
    points_redeemed: 300,
  },
  {
    id: 'b-3',
    trip_name: 'Budimpešta Sretenje 2026',
    destination: 'Budimpešta',
    country: 'Mađarska',
    region: 'central_europe',
    start_date: '2026-02-14',
    end_date: '2026-02-16',
    status: 'Završeno',
    amount_eur: 189,
    points_earned: 227,
    points_redeemed: 0,
  },
  {
    id: 'b-4',
    trip_name: 'Eurotrip Amsterdam Proleće',
    destination: 'Amsterdam',
    country: 'Holandija',
    region: 'western_europe',
    start_date: '2026-04-22',
    end_date: '2026-04-27',
    status: 'Predstojeće',
    amount_eur: 389,
    points_earned: 0,
    points_redeemed: 200,
  },
  {
    id: 'b-5',
    trip_name: 'Go2 Lefkada Leto 2026',
    destination: 'Lefkada',
    country: 'Grčka',
    region: 'mediterranean',
    start_date: '2026-07-18',
    end_date: '2026-07-29',
    status: 'Predstojeće',
    amount_eur: 329,
    points_earned: 0,
    points_redeemed: 0,
  },
];

export const MOCK_TRANSACTIONS: PointTransaction[] = [
  {
    id: 't-1',
    date: '2025-07-21',
    type: 'Zarada - Putovanje',
    description: 'Bodovi od putovanja Go2 Kavos Leto 2025',
    points: 349,
  },
  {
    id: 't-2',
    date: '2025-09-01',
    type: 'Zarada - Bonus',
    description: 'Bonus za preporuku (Prijatelj: Sara)',
    points: 300,
  },
  {
    id: 't-3',
    date: '2025-10-15',
    type: 'Zarada - Izazov',
    description: 'Izazov: Mediteranski Istraživač',
    points: 200,
  },
  {
    id: 't-4',
    date: '2026-01-05',
    type: 'Iskorišćeno',
    description: 'Iskorišćeno za Go2 Grudvanje Les Orres',
    points: -300,
  },
  {
    id: 't-5',
    date: '2026-01-20',
    type: 'Zarada - Putovanje',
    description: 'Bodovi od putovanja Go2 Grudvanje Les Orres (1.2x)',
    points: 539,
  },
  {
    id: 't-6',
    date: '2026-02-08',
    type: 'Zarada - Check-in',
    description: 'Nedeljni check-in bonus (Streak x5)',
    points: 25,
  },
  {
    id: 't-7',
    date: '2026-02-10',
    type: 'Iskorišćeno',
    description: 'Iskorišćeno za Eurotrip Amsterdam',
    points: -200,
  },
  {
    id: 't-8',
    date: '2026-02-11',
    type: 'Zarada - Spin',
    description: 'Točak Sreće — dobitak!',
    points: 75,
  },
  {
    id: 't-9',
    date: '2026-02-16',
    type: 'Zarada - Putovanje',
    description: 'Bodovi od putovanja Budimpešta Sretenje (1.2x)',
    points: 227,
  },
  {
    id: 't-10',
    date: '2026-02-12',
    type: 'Zarada - Bonus',
    description: 'Bonus za preporuku (Prijatelj: Nikola)',
    points: 300,
  },
];

export const MOCK_ACHIEVEMENTS: Achievement[] = [
  // Putovanje
  {
    id: 'a-1',
    name: 'Go2 Putnik',
    description: 'Završi svoje prvo putovanje sa Go2',
    reward_points: 100,
    icon: 'Plane',
    unlocked_date: '2025-07-20',
    is_unlocked: true,
    category: 'putovanje',
  },
  {
    id: 'a-2',
    name: 'Go2 Veteran',
    description: 'Završi 3 putovanja sa Go2',
    reward_points: 300,
    icon: 'Map',
    unlocked_date: '2026-02-16',
    is_unlocked: true,
    category: 'putovanje',
  },
  {
    id: 'a-3',
    name: 'Globtrotter',
    description: 'Poseti 5 različitih zemalja sa Go2',
    reward_points: 500,
    icon: 'Globe',
    is_unlocked: false,
    category: 'putovanje',
  },
  // Društveno
  {
    id: 'a-4',
    name: 'Go2 Ekipa',
    description: 'Rezerviši putovanje za 3+ osobe',
    reward_points: 150,
    icon: 'Users',
    unlocked_date: '2025-07-10',
    is_unlocked: true,
    category: 'društveno',
  },
  {
    id: 'a-5',
    name: 'Ambasador',
    description: 'Preporuči Go2 za 5 prijatelja',
    reward_points: 500,
    icon: 'Heart',
    is_unlocked: false,
    category: 'društveno',
  },
  {
    id: 'a-6',
    name: 'Party Leader',
    description: 'Organizuj grupu od 10+ putnika za Go2',
    reward_points: 750,
    icon: 'PartyPopper',
    is_unlocked: false,
    category: 'društveno',
  },
  // Istraživač
  {
    id: 'a-7',
    name: 'Mediteranac',
    description: 'Poseti 2 mediteranske destinacije sa Go2',
    reward_points: 200,
    icon: 'Sun',
    is_unlocked: false,
    category: 'istraživač',
  },
  {
    id: 'a-8',
    name: 'Alpski Šampion',
    description: 'Idi na Go2 Grudvanje u francuske Alpe',
    reward_points: 200,
    icon: 'Mountain',
    unlocked_date: '2026-01-19',
    is_unlocked: true,
    category: 'istraživač',
  },
  {
    id: 'a-9',
    name: 'Sva Godišnja Doba',
    description: 'Putuj i leto i zimu u istoj godini sa Go2',
    reward_points: 400,
    icon: 'SunSnow',
    is_unlocked: false,
    category: 'sezona',
  },
  // Potrošač
  {
    id: 'a-10',
    name: 'Go2 VIP',
    description: 'Potroši 1500+ EUR sa Go2 u jednoj godini',
    reward_points: 500,
    icon: 'Coins',
    is_unlocked: false,
    category: 'potrošač',
  },
  {
    id: 'a-11',
    name: 'Streak Master',
    description: 'Ostvari 30-dnevni check-in streak',
    reward_points: 300,
    icon: 'Flame',
    is_unlocked: false,
    category: 'potrošač',
  },
  {
    id: 'a-12',
    name: 'Bingo Šampion',
    description: 'Završi pun red na Destinacijskom Bingu',
    reward_points: 350,
    icon: 'Grid3x3',
    is_unlocked: false,
    category: 'istraživač',
  },
];

// === GAMIFICATION MOCK DATA ===

export const MOCK_CHECK_IN_HISTORY: DailyCheckIn[] = [
  { date: '2026-02-07', points: 5, streakDay: 1 },
  { date: '2026-02-08', points: 5, streakDay: 2 },
  { date: '2026-02-09', points: 10, streakDay: 3 },
  { date: '2026-02-10', points: 10, streakDay: 4 },
  { date: '2026-02-11', points: 15, streakDay: 5 },
];

export const MOCK_TRAVEL_STAMPS: TravelStamp[] = [
  { id: 's-1', destination: 'Kavos (Krf)', country: 'Grčka', region: 'mediterranean', date: '2025-07-20', emoji: '🏖️', tripName: 'Go2 Kavos Leto 2025' },
  { id: 's-2', destination: 'Les Orres', country: 'Francuska', region: 'western_europe', date: '2026-01-19', emoji: '⛷️', tripName: 'Go2 Grudvanje Les Orres' },
  { id: 's-3', destination: 'Budimpešta', country: 'Mađarska', region: 'central_europe', date: '2026-02-16', emoji: '🏛️', tripName: 'Budimpešta Sretenje 2026' },
];

export const MOCK_SEASONAL_CHALLENGES: SeasonalChallenge[] = [
  {
    id: 'ch-1',
    title: 'Prolećni City Break',
    description: 'Rezerviši prolećno putovanje u evropski grad pre 1. aprila',
    icon: 'Flower2',
    type: 'seasonal',
    reward: 200,
    progress: 1,
    target: 1,
    deadline: '2026-04-01',
    completed: true,
  },
  {
    id: 'ch-2',
    title: 'Go2 Ekipa',
    description: 'Pozovi 3 prijatelja preko referral koda',
    icon: 'Users',
    type: 'social',
    reward: 400,
    progress: 3,
    target: 3,
    deadline: '2026-06-30',
    completed: true,
  },
  {
    id: 'ch-3',
    title: 'Dva Mora, Jedno Leto',
    description: 'Poseti 2 različite plaže tokom leta 2026 (Lefkada, Kavos, Zakintos, Budva...)',
    icon: 'MapPin',
    type: 'explorer',
    reward: 500,
    progress: 0,
    target: 2,
    deadline: '2026-09-30',
    completed: false,
  },
  {
    id: 'ch-4',
    title: 'Letnji Go2 Budžet',
    description: 'Potroši 800+ EUR na letnja putovanja sa Go2',
    icon: 'Banknote',
    type: 'spender',
    reward: 600,
    progress: 329,
    target: 800,
    deadline: '2026-09-30',
    completed: false,
  },
  {
    id: 'ch-5',
    title: 'Check-in Ratnik',
    description: 'Ostvari 14-dnevni streak check-in',
    icon: 'Flame',
    type: 'social',
    reward: 150,
    progress: 5,
    target: 14,
    deadline: '2026-03-31',
    completed: false,
  },
  {
    id: 'ch-6',
    title: 'Balkanski Obilazak',
    description: 'Poseti 3 balkanske destinacije (Budva, Ohrid, Sarajevo...)',
    icon: 'Compass',
    type: 'explorer',
    reward: 450,
    progress: 0,
    target: 3,
    deadline: '2026-12-31',
    completed: false,
  },
];

export const MOCK_BINGO: BingoCell[] = [
  { id: 1, destination: 'Grčka', country: 'Grčka', visited: true },
  { id: 2, destination: 'Turska', country: 'Turska', visited: false },
  { id: 3, destination: 'Crna Gora', country: 'Crna Gora', visited: false },
  { id: 4, destination: 'Francuska', country: 'Francuska', visited: true },
  { id: 5, destination: 'Mađarska', country: 'Mađarska', visited: true },
  { id: 6, destination: 'Češka', country: 'Češka', visited: false },
  { id: 7, destination: 'Holandija', country: 'Holandija', visited: false },
  { id: 8, destination: 'Makedonija', country: 'Makedonija', visited: false },
  { id: 9, destination: 'Bosna', country: 'Bosna', visited: false },
];

export const MOCK_LEADERBOARD: LeaderboardEntry[] = [
  { rank: 1, name: 'Marko S.', initials: 'MS', points: 4200, level: 'Legend', isCurrentUser: false, trips: 8 },
  { rank: 2, name: 'Ana P.', initials: 'AP', points: 3800, level: 'Legend', isCurrentUser: false, trips: 7 },
  { rank: 3, name: 'Nikola T.', initials: 'NT', points: 3100, level: 'Legend', isCurrentUser: false, trips: 6 },
  { rank: 4, name: 'Jovana M.', initials: 'JM', points: 2800, level: 'World Traveler', isCurrentUser: false, trips: 5 },
  { rank: 5, name: 'Stefan R.', initials: 'SR', points: 2600, level: 'World Traveler', isCurrentUser: false, trips: 5 },
  { rank: 6, name: 'Aleksa P.', initials: 'AP', points: 2450, level: 'World Traveler', isCurrentUser: true, trips: 5 },
  { rank: 7, name: 'Milica D.', initials: 'MD', points: 2200, level: 'World Traveler', isCurrentUser: false, trips: 4 },
  { rank: 8, name: 'Luka V.', initials: 'LV', points: 1900, level: 'World Traveler', isCurrentUser: false, trips: 4 },
  { rank: 9, name: 'Sara K.', initials: 'SK', points: 1500, level: 'Traveler', isCurrentUser: false, trips: 3 },
  { rank: 10, name: 'Petar N.', initials: 'PN', points: 1200, level: 'Traveler', isCurrentUser: false, trips: 2 },
];

export const MOCK_SPIN_PRIZES: SpinWheelPrize[] = [
  { id: 1, label: '50 bodova', points: 50, color: '#EE4877' },
  { id: 2, label: '25 bodova', points: 25, color: '#009fe3' },
  { id: 3, label: '100 bodova', points: 100, color: '#d4e157' },
  { id: 4, label: '10 bodova', points: 10, color: '#8B5CF6' },
  { id: 5, label: '75 bodova', points: 75, color: '#EE4877' },
  { id: 6, label: '200 bodova', points: 200, color: '#10B981' },
  { id: 7, label: '15 bodova', points: 15, color: '#009fe3' },
  { id: 8, label: '150 bodova', points: 150, color: '#F59E0B' },
];

// === UPSELL MOCK DATA (matched to Go2's real upsell opportunities) ===

export const MOCK_EXPERIENCES: Experience[] = [
  {
    id: 'exp-1',
    title: 'Vodič po Starom Gradu',
    description: 'Licencirani lokalni vodič te vodi kroz stari grad — skriveni ćoškovi, istorija, lokalne priče. Dostupno na svim Go2 destinacijama.',
    destination: 'Sve destinacije',
    category: 'guide',
    price: 35,
    bonusPoints: 80,
    pointsMultiplier: 2.0,
    emoji: '🗣️',
    rating: 4.9,
    duration: '3 sata',
    popular: true,
  },
  {
    id: 'exp-2',
    title: 'Food Tour — Lokalna Kuhinja',
    description: '5 restorana, 5 ukusa, 1 nezaboravno veče. Probaj autentična jela i lokalna vina uz profesionalnog food guida u Atini.',
    destination: 'Atina',
    category: 'food',
    price: 55,
    bonusPoints: 120,
    pointsMultiplier: 1.5,
    emoji: '🍽️',
    rating: 4.8,
    duration: '4 sata',
    popular: true,
  },
  {
    id: 'exp-3',
    title: 'Boat Cruise Boka Kotorska',
    description: 'Krstarenje Bokom Kotorskom iz Budve — poseta ostrvima, kupanje u uvalama, panoramski pogled na crnogorsko primorje.',
    destination: 'Budva',
    category: 'nature',
    price: 45,
    bonusPoints: 100,
    pointsMultiplier: 1.5,
    emoji: '🚢',
    rating: 4.7,
    duration: '5 sati',
    popular: true,
  },
  {
    id: 'exp-4',
    title: 'Party Paket — VIP Ulaz',
    description: 'VIP ulaz u najbolje klubove na destinaciji. Preskoči red, besplatno piće dobrodošlice, rezervisani sto. Kavos, Budva, Les 2 Alpes.',
    destination: 'Kavos / Budva',
    category: 'nightlife',
    price: 30,
    bonusPoints: 70,
    pointsMultiplier: 1.5,
    emoji: '🎶',
    rating: 4.6,
    duration: 'Celu noć',
    popular: true,
  },
  {
    id: 'exp-5',
    title: 'Privatni Ski Instruktor',
    description: 'Privatni ski instruktor na 3 sata u Les Orres ili Les 2 Alpes. Bilo da si početnik ili napredni — podignite tehniku na viši nivo.',
    destination: 'Les Orres / Les 2 Alpes',
    category: 'adventure',
    price: 85,
    bonusPoints: 180,
    pointsMultiplier: 2.0,
    emoji: '⛷️',
    rating: 4.8,
    duration: '3 sata',
    popular: true,
  },
  {
    id: 'exp-6',
    title: 'Fotografski Paket',
    description: 'Profesionalni fotograf za tvoje putovanje. 50+ obrađenih fotografija — uspomene za ceo život. Dostupno na svim destinacijama.',
    destination: 'Sve destinacije',
    category: 'culture',
    price: 120,
    bonusPoints: 250,
    pointsMultiplier: 1.5,
    emoji: '📸',
    rating: 4.9,
    duration: '2 sata',
    popular: false,
  },
  {
    id: 'exp-7',
    title: 'Upgrade na Avion',
    description: 'Umesto 20h autobusom — doleti za 3 sata. Upgrade na avionski prevoz za Kušadasi ili Marmaris. Udobnije, brže, bez stresa.',
    destination: 'Kušadasi / Marmaris',
    category: 'guide',
    price: 150,
    bonusPoints: 300,
    pointsMultiplier: 2.0,
    emoji: '✈️',
    rating: 5.0,
    duration: '—',
    popular: false,
  },
  {
    id: 'exp-8',
    title: 'Sunset Jedrenje Zakintos',
    description: 'Privatno jedrenje uz zalazak sunca oko ostrva Zakintos. Kupanje u Navagio Bay, šampanjac na brodu, nezaboravan pogled.',
    destination: 'Zakintos',
    category: 'nature',
    price: 95,
    bonusPoints: 200,
    pointsMultiplier: 2.0,
    emoji: '⛵',
    rating: 5.0,
    duration: '3 sata',
    popular: true,
  },
];

export const MOCK_MULTIPLIER_EVENTS: PointsMultiplierEvent[] = [
  {
    id: 'me-1',
    title: 'Valentinovo — Dupli Bodovi!',
    description: 'Samo ovog vikenda: sva rezervisana putovanja nose 2x bodova! Idealno za Sretenje ponude.',
    multiplier: 2.0,
    startDate: '2026-02-14',
    endDate: '2026-02-16',
    active: true,
    type: 'flash',
    emoji: '💝',
  },
  {
    id: 'me-2',
    title: 'Prolećna Groznica — 1.5x Bodovi',
    description: 'Rezerviši prolećno putovanje i osvoji 50% više bodova! Amsterdam, Pariz, Toskana...',
    multiplier: 1.5,
    startDate: '2026-03-01',
    endDate: '2026-03-31',
    active: false,
    type: 'seasonal',
    emoji: '🌸',
  },
  {
    id: 'me-3',
    title: 'Guarana x Go2 — Tripli Grčka!',
    description: 'U saradnji sa Guaranom — sve grčke destinacije (Lefkada, Kavos, Zakintos, Evia) nose 3x bodova!',
    multiplier: 3.0,
    startDate: '2026-06-01',
    endDate: '2026-06-30',
    active: false,
    type: 'partner',
    emoji: '🇬🇷',
  },
];

export const MOCK_BUNDLES: TravelBundle[] = [
  {
    id: 'bun-1',
    name: 'Leto + Zima Kombo',
    trips: ['Go2 Lefkada Leto 2026', 'Go2 Grudvanje Les Orres 2027'],
    originalPrice: 750,
    bundlePrice: 650,
    bonusPoints: 300,
    pointsMultiplier: 1.5,
    emoji: '🌞❄️',
  },
  {
    id: 'bun-2',
    name: 'Balkanski Grand Tour',
    trips: ['Budva Leto', 'Ohrid Vikend', 'Sarajevo City Break'],
    originalPrice: 550,
    bundlePrice: 470,
    bonusPoints: 400,
    pointsMultiplier: 2.0,
    emoji: '🏔️🏖️🕌',
  },
  {
    id: 'bun-3',
    name: 'Mediteranski Paket',
    trips: ['Lefkada Leto', 'Kavos Party', 'Zakintos Leto'],
    originalPrice: 900,
    bundlePrice: 780,
    bonusPoints: 600,
    pointsMultiplier: 2.0,
    emoji: '🌊☀️🏖️',
  },
  {
    id: 'bun-4',
    name: 'Godišnji Go2 All-In',
    trips: ['Prolećni Eurotrip', 'Letovanje Grčka', 'Go2 Grudvanje Francuska'],
    originalPrice: 1200,
    bundlePrice: 980,
    bonusPoints: 1000,
    pointsMultiplier: 2.5,
    emoji: '🌸☀️❄️',
  },
];
